import { useState } from "react";
import { MoveHorizontal, Play } from "lucide-react";
import { HERO_SIZES, heroSrcSet, mediaUrl } from "../lib/api";
import { Illustration, variantOf } from "./Illustration";

export const embedUrl = (video) => {
  if (!video) return null;
  if (video.provider === "youtube") return `https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`;
  return `https://player.vimeo.com/video/${video.id}?autoplay=1&dnt=1`;
};

const Img = ({ src, p, muted }) =>
  src ? <img src={mediaUrl(src)} srcSet={heroSrcSet(src)} sizes={HERO_SIZES} alt="" decoding="async" fetchPriority="high"
    style={{ width: "100%", height: "100%", objectFit: "cover" }} draggable={false} data-zoomable="" />
    : <Illustration variant={variantOf(p)} muted={muted} />;

const Label = ({ side, children }) => (
  <span style={{ position: "absolute", top: 20, [side]: 20, background: side === "left" ? "rgba(20,35,42,.82)" : "#C8102E", color: "#fff",
    fontSize: 13, fontWeight: 600, padding: "7px 12px", borderRadius: 999, pointerEvents: "none", zIndex: 3 }}>{children}</span>
);

export const BeforeAfter = ({ p, before, after, ai }) => {
  const [pos, setPos] = useState(50);
  return (
    <div style={{ position: "absolute", inset: 0 }} data-testid="before-after-slider">
      <div style={{ position: "absolute", inset: 0 }}><Img src={after} p={p} /></div>
      <div style={{ position: "absolute", inset: 0, clipPath: `inset(0 ${100 - pos}% 0 0)` }}><Img src={before} p={p} muted={!before} /></div>
      <Label side="left">Before</Label>
      <Label side="right">{ai ? "After · AI concept" : "After"}</Label>
      <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))} data-testid="before-after-range"
        aria-label="Before and after comparison. Move left or right to reveal more of each image."
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", margin: 0, opacity: 0, cursor: "ew-resize", zIndex: 2 }} />
      <div aria-hidden="true" style={{ position: "absolute", top: 0, bottom: 0, left: `${pos}%`, width: 0, pointerEvents: "none", zIndex: 1 }}>
        <div style={{ position: "absolute", top: 0, bottom: 0, left: -1.5, width: 3, background: "#fff" }} />
        <div style={{ position: "absolute", top: "50%", left: -26, marginTop: -26, width: 52, height: 52, borderRadius: "50%", background: "#fff",
          boxShadow: "0 4px 14px rgba(20,35,42,.3)", display: "flex", alignItems: "center", justifyContent: "center", color: "#14232a" }}>
          <MoveHorizontal size={22} />
        </div>
      </div>
    </div>
  );
};

export const VideoPoster = ({ video, label = "Play video", big = true }) => {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return <iframe title={label} src={embedUrl(video)} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} data-testid="video-embed" data-zoomable="" />;
  }
  const poster = video?.thumbnail;
  return (
    <button type="button" onClick={() => setPlaying(true)} aria-label={label} data-testid="video-play-btn"
      style={{ position: "absolute", inset: 0, width: "100%", border: "none", cursor: "pointer", background: "#1f3540", color: "#fff", padding: 0,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, font: "600 15px Inter, sans-serif" }}>
      {poster && (
        <>
          <img src={mediaUrl(poster)} srcSet={heroSrcSet(poster)} sizes={HERO_SIZES} alt="" decoding="async" fetchPriority="high" data-testid="video-poster-img"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} draggable={false} data-zoomable="" />
          <span aria-hidden="true" style={{ position: "absolute", inset: 0, background: "rgba(20, 35, 42, .28)" }} />
        </>
      )}
      <span style={{ position: "relative", width: big ? 72 : 56, height: big ? 72 : 56, borderRadius: "50%", background: "#fff", color: "#14232a", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(0, 0, 0, .25)" }}>
        <Play size={big ? 28 : 22} fill="currentColor" />
      </span>
      {big && <span style={{ position: "relative", textShadow: poster ? "0 1px 6px rgba(0, 0, 0, .6)" : "none" }}>{label} · {video?.provider === "vimeo" ? "Vimeo" : "YouTube"}</span>}
    </button>
  );
};

export const HeaderMedia = ({ p, outline = false, children, ratio = "1200 / 560", flat = false, testId = "header-media" }) => (
  <section aria-label="Proposal header media" data-testid={testId}
    style={{ position: "relative", width: "100%", aspectRatio: ratio, borderRadius: flat ? 0 : 18, overflow: "hidden", background: "#dbe7ec",
      outline: outline ? "1.5px dashed #e79aa5" : "none", outlineOffset: 5 }}>
    {p.header_type === "video" && p.video ? <VideoPoster video={p.video} /> : null}
    {p.header_type === "image" ? (
      <div style={{ position: "absolute", inset: 0 }}>
        <Img src={p.hero_image} p={p} />
        {p.ai_generated && <Label side="right">AI concept</Label>}
      </div>
    ) : null}
    {p.header_type === "slider" ? <BeforeAfter p={p} before={p.before_image} after={p.after_image} ai={p.ai_generated} /> : null}
    {p.header_type === "video" && !p.video ? <div style={{ position: "absolute", inset: 0, background: "#1f3540" }} /> : null}
    {children}
  </section>
);
